import {
  Box,
  Flex,
  HStack,
  Link,
  IconButton,
  useDisclosure,
  useColorModeValue,
  VStack,
  Text,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import { ColorModeSwitcher } from "./ColorModeSwitcher";

const Links = [
  { label: "Home", to: "/" },
  { label: "Projects", to: "/projects" },
];

function NavLink({ link }: { link: { label: string; to: string } }) {
  const location = useLocation();
  const isActive =
    location.pathname === link.to || (link.to === "/" && location.pathname === "/");
  const inactiveColor = useColorModeValue("gray.600", "gray.400");
  const activeColor = "accent.400";

  return (
    <Link
      as={RouterLink}
      to={link.to}
      px={3}
      py={2}
      rounded="md"
      fontSize="sm"
      fontWeight={isActive ? "600" : "500"}
      color={isActive ? activeColor : inactiveColor}
      _hover={{
        textDecoration: "none",
        color: activeColor,
      }}
      position="relative"
      _after={
        isActive
          ? {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 3,
              right: 3,
              height: "2px",
              bg: "accent.400",
              borderRadius: "full",
            }
          : undefined
      }
    >
      {link.label}
    </Link>
  );
}

export default function NavigationBar() {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const bg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const mobileLinkHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const logoColor = useColorModeValue("gray.800", "white");

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={10}
      bg={bg}
      borderBottomWidth="1px"
      borderColor={borderColor}
      backdropFilter="blur(12px)"
    >
      <Flex
        maxW="6xl"
        mx="auto"
        h={16}
        px={{ base: 4, md: 6 }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Link
          as={RouterLink}
          to="/"
          fontSize="lg"
          fontWeight="700"
          letterSpacing="-0.02em"
          color={logoColor}
          _hover={{ textDecoration: "none", opacity: 0.85 }}
        >
          LG
        </Link>

        <HStack as="nav" spacing={1} display={{ base: "none", md: "flex" }}>
          {Links.map((link) => (
            <NavLink key={link.label} link={link} />
          ))}
        </HStack>

        <HStack spacing={2}>
          <ColorModeSwitcher aria-label="Toggle theme" />
          <IconButton
            size="md"
            icon={isOpen ? <FiX /> : <FiMenu />}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            display={{ base: "inline-flex", md: "none" }}
            variant="ghost"
            onClick={onToggle}
          />
        </HStack>
      </Flex>

      {isOpen && (
        <Box
          pb={4}
          px={4}
          display={{ md: "none" }}
          borderTopWidth="1px"
          borderColor={borderColor}
        >
          <VStack as="nav" spacing={0} align="stretch" pt={2}>
            {Links.map((link) => (
              <Link
                key={link.label}
                as={RouterLink}
                to={link.to}
                px={4}
                py={3}
                rounded="md"
                fontWeight="500"
                _hover={{ bg: mobileLinkHoverBg }}
                onClick={onClose}
              >
                {link.label}
              </Link>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
